import type { ReactNode } from "react"
import type { ToolCallStatus } from "@/components/chat/types"

export interface ToolRenderProps {
  name: string
  args: string
  status: ToolCallStatus
  result?: string
}

export type ToolRenderFn = (props: ToolRenderProps) => ReactNode

interface ToolRendererEntry {
  render: ToolRenderFn
  /** When true, this tool renders a larger widget and feedback buttons should go below. */
  isWidget: boolean
}

const renderers = new Map<string, ToolRendererEntry>()

/**
 * Register the default (wildcard) tool renderer, used for any tool
 * without a specific renderer. Always treated as non-widget.
 *
 * @param render - The render function for the default tool display.
 */
export function useDefaultTool(render: ToolRenderFn): void {
  renderers.set("*", { render, isWidget: false })
}

/**
 * Register a renderer for a specific tool name.
 *
 * @param name - The tool name to match.
 * @param render - The render function for this tool.
 * @param options - Optional configuration.
 * @param options.isWidget - When true, indicates this tool renders a larger
 *   widget (e.g. a table, card, or report) rather than a compact inline element.
 *   Affects how feedback buttons are positioned around the rendered output.
 */
export function useToolRenderer(
  name: string,
  render: ToolRenderFn,
  options?: { isWidget?: boolean }
): void {
  renderers.set(name, { render, isWidget: options?.isWidget ?? false })
}

/**
 * Register a widget tool renderer. Shorthand for `useToolRenderer` with
 * `isWidget: true` — use this for tools that render larger visual components
 * (tables, cards, reports) rather than compact inline displays.
 *
 * @param name - The tool name to match.
 * @param render - The render function for this tool.
 */
export function useWidgetToolRenderer(name: string, render: ToolRenderFn): void {
  renderers.set(name, { render, isWidget: true })
}

/**
 * Look up the render function for a tool by name.
 * Falls back to the wildcard "*" renderer if no specific match exists.
 *
 * @param name - The tool name to look up.
 * @returns The render function, or null if no renderer is registered.
 */
export function getToolRenderer(name: string): ToolRenderFn | null {
  const entry = renderers.get(name) ?? renderers.get("*")
  return entry?.render ?? null
}

/**
 * Check whether a tool is registered as a widget renderer.
 *
 * @param name - The tool name to check.
 * @returns True if the tool has a registered renderer with isWidget: true.
 */
export function isWidgetTool(name: string): boolean {
  const entry = renderers.get(name)
  return entry?.isWidget ?? false
}
